import { useEffect, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import type { ColumnDef, PaginationState, Table as ReactTableType, Updater } from "@tanstack/react-table";
import { useTanStackTable } from "@steelyard/theme";
import { DEFAULT_PAGE_SIZE, readPaging } from "../../lib/paging";
import { TableToolbar, type StatusFilterProps } from "./toolbar";
import { actionsFirst, restoreTableState, TableFrame, TableView, useTableMemory } from "./table-view";

export type { StatusOption } from "./toolbar";

/**
 * THE list pattern, COPIED from
 * `vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-list/table.tsx`.
 *
 * That file is four things in a row, and this is the same four:
 *
 *   const { table } = useTanStackTable({ tableData, columnConfig, options })
 *   <Filters table={table} />                      → `TableToolbar` (./toolbar.tsx)
 *   <Table table={table} variant="modern" … />     → `TableView` (./table-view.tsx)
 *   <TablePagination table={table} />              → inside `TableView`'s footer
 *
 * laid out as the Marketplace admin portal's grids (2026-09-28): the toolbar flush on the page, the
 * table in a bordered frame under it (`TableFrame`, ./table-view.tsx). The page header above both —
 * title, the API's count, description — is `ListHeader` (./list-header.tsx), composed by the screen;
 * the screen's own buttons (New …, links to sibling screens) go in `actions`, at the end of the
 * toolbar row.
 *
 * ## Paging lives in the URL: `?page=` and `?pageSize=` (default 10)
 *
 * The template's tables page client-side over a static array (`@/data/order-data`). Most lists here
 * are server-paged: the route loader reads `lib/paging.ts`'s `readPaging` and fetches that one
 * slice. For those, `manualPagination: true`, `pageCount` from the API, and `onPaginationChange`
 * turns the template's own pager buttons and "Rows per page" select into a search-param change the
 * loader refetches on, keeping every other param (the filters) as it was. `TablePagination` itself
 * is untouched; it calls `table.nextPage()` / `table.setPageSize()` and this decides what that means.
 *
 * A list whose WHOLE set arrived in one response (the category tree, a filter narrowed to fewer rows
 * than a page) is paged here instead, with the same page-size choice. Detected from the envelope,
 * not declared: page 1 carrying at least `totalCount` rows means the server holds nothing more. It
 * is also what makes a `table.setGlobalFilter` search box honest there: it searches every row.
 *
 * `useTanStackTable` seeds its rows with `useState([...tableData])`, which never updates again.
 * That is right for the template (its array is a module constant) and wrong here (a new page
 * arrives on every navigation), so the effect below re-seeds it through `setData`, which is part of
 * the hook's own returned API. Nothing in the copied file changed.
 *
 * ## The toolbar
 *
 * One layout for every list (./toolbar.tsx): the status filter as a DROPDOWN (`status`), then the
 * search box (`search`), then the module's own filters (`filters`), each labelled above its control;
 * then Reset (`clear`), the column toggle and the screen's `actions`, right-aligned. A status option
 * carries its count exactly as the screen got it: the API's number, or none. One control per filter:
 * a filter that is the status dropdown is not also a drawer field.
 *
 * ## Sorting: any column with an accessor, and never silently page-local
 *
 * No service accepts a `sort` parameter; each returns one fixed order (orders and invoices newest
 * first). So a header sort on a server-paged list can only reorder the page on screen — which the
 * footer then says in words ("sorted within this page · Reset"). On a whole-set list the same sort
 * covers every row. A column opts out with `enableSorting: false` where its order is meaning, not
 * preference (the category tree's parent-before-child order).
 */

export type PagedResult<T> = {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages?: number;
  hasMore?: boolean;
};

type Slot<T> = ReactNode | ((table: ReactTableType<T>) => ReactNode);

type PagedTableProps<T> = {
  result: PagedResult<T>;
  columns: ColumnDef<T, unknown>[];
  empty: ReactNode;
  caption?: string;
  /**
   * A heading inside a card — for a screen with more than one table ("By SKU") or a table
   * embedded in a detail page. A list screen's count is its `ListHeader`'s, not repeated here.
   */
  title?: ReactNode;
  description?: ReactNode;
  /** The screen's own buttons: at the end of the toolbar row, or in the card's heading when titled. */
  actions?: ReactNode;
  /** The status filter, as a dropdown. Each option is a link built with `hrefWith`. */
  status?: StatusFilterProps;
  /** The search box: `SearchInput` over a server param, or `(table) => <TableSearch table={table} … />`. */
  search?: Slot<T>;
  /**
   * The module's own secondary filter fields. Either a plain node or a render function handed
   * PagedTable's own `table` instance, the way vendor's order-list/table.tsx hands `table` to its
   * `<Filters table={table} />`.
   */
  filters?: Slot<T>;
  /** `active` is the screen's own "is anything filtered" rule; `href` is its unfiltered URL. */
  clear?: { active: boolean; href: string };
  getRowId?: (row: T, index: number) => string;
  /** A full-width row after a given row - an inline defect banner, a duplicate group's candidates. */
  subRow?: (row: T) => ReactNode;
  /** Emphasis on a row the API flagged - e.g. a BR-07 invoice defect. Never computed here. */
  rowClassName?: (row: T) => string | undefined;
};

export function PagedTable<T extends Record<string, unknown>>({
  result,
  columns,
  empty,
  caption,
  title,
  description,
  actions,
  status,
  search,
  filters,
  clear,
  getRowId,
  subRow,
  rowClassName,
}: PagedTableProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, totalCount } = result;
  const serverPaged = page > 1 || result.items.length < totalCount;
  // Server-paged: the envelope's own page and size are the truth. Otherwise the URL's size, over
  // the rows already here.
  const pageSize = serverPaged ? result.pageSize : readPaging(searchParams).pageSize;
  // Row-count arithmetic, not money. Same line `@steelyard/ui`'s PagedTable always had; the guard
  // suite exempts it by name with that reason.
  const totalPages = result.totalPages ?? (totalCount <= 0 ? 1 : Math.ceil(totalCount / result.pageSize));

  const writeParams = (over: { page?: number; pageSize?: number }) =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if ("page" in over) {
        if (over.page && over.page > 1) next.set("page", String(over.page));
        else next.delete("page");
      }
      if (over.pageSize !== undefined) {
        if (over.pageSize === DEFAULT_PAGE_SIZE) next.delete("pageSize");
        else next.set("pageSize", String(over.pageSize));
      }
      return next;
    });

  // `useTanStackTable` seeds `columnOrder` from each column's `id`; an `accessorKey`-only column has
  // none yet, so it sorted after every id'd column (the Tally queue's actions column rendered first).
  // Give it TanStack's own derived id up front.
  const liveColumns = actionsFirst(
    columns.map((column) =>
      column.id ? column : { ...column, id: String((column as { accessorKey?: string }).accessorKey ?? column.header) },
    ),
  );

  const { table, setData } = useTanStackTable<T>({
    tableData: result.items,
    columnConfig: liveColumns,
    options: {
      // The hook freezes `columnConfig` in a mount-time `useState`, so a cell reading a prop that
      // changes later (the tier-price row's expand toggle) would render its first value forever.
      // Its own `...options` spread lands after that frozen copy, so the current columns go here.
      ...({ columns: liveColumns } as object),
      manualPagination: serverPaged,
      pageCount: serverPaged ? totalPages : undefined,
      initialState: { pagination: { pageIndex: serverPaged ? page - 1 : 0, pageSize } },
      onPaginationChange: (updater: Updater<PaginationState>) => {
        // `useTanStackTable` Omits `state` from what a caller may pass, so pagination can't be a
        // controlled prop here, and `table.setPagination`/`setPageSize` both call straight back into
        // this handler. `table.setState` is the one entry point that writes state without recursing
        // - the same reason the rows effect below uses the hook's raw `setData`.
        const current = table.getState().pagination;
        const next = typeof updater === "function" ? updater(current) : updater;
        if (next.pageSize !== current.pageSize) {
          // A different page size is a different slicing of the list: back to its first page.
          // `next.pageIndex` is TanStack's recentred index (keep the same top row), meaningless once
          // the server re-slices, so it is dropped.
          writeParams({ page: undefined, pageSize: next.pageSize });
          return;
        }
        if (next.pageIndex === current.pageIndex) return;
        if (serverPaged) writeParams({ page: next.pageIndex + 1 });
        else table.setState((old) => ({ ...old, pagination: next }));
      },
      enableColumnResizing: false,
      // A list paged here keeps its page across a refresh of the same rows (packing's 30 s poll)
      // and a return to it (`useTableMemory`); a different URL starts it on page 1 (below).
      autoResetPageIndex: false,
      getRowId,
    },
  });
  const restored = useTableMemory(table, caption);
  const query = searchParams.toString();

  useEffect(() => {
    setData([...result.items]);
  }, [result.items, setData]);

  useEffect(() => {
    // React Router does not remount PagedTable on a same-route "?page=" change, so without this the
    // pager's "Page X of Y" and prev/next `disabled` go stale on any in-place navigation. Re-synced
    // the same way `setData` above re-seeds rows: imperatively, on the values that changed. A new
    // query (a filter) on a list paged here is a new set of rows, from its first page.
    table.setState((old) => ({ ...old, pagination: { pageIndex: serverPaged ? page - 1 : 0, pageSize } }));
  }, [serverPaged, page, pageSize, query, table]);

  useEffect(() => {
    // Returned to: the search, sort and in-memory page as they were left. After the sync above, once.
    if (!restored) return;
    restoreTableState(table, restored);
    if (!serverPaged) table.setState((old) => ({ ...old, pagination: { ...old.pagination, pageIndex: restored.pageIndex } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slot = (content: Slot<T> | undefined) => (typeof content === "function" ? content(table) : content);

  return (
    <TableFrame
      title={title}
      description={description}
      action={title || description ? actions : undefined}
      toolbar={(inset) => (
        <TableToolbar
          table={table}
          inset={inset}
          status={status}
          search={slot(search)}
          filters={slot(filters)}
          clear={clear}
          actions={title || description ? undefined : actions}
        />
      )}
    >
      {result.items.length === 0 ? (
        <div className="p-4">{empty}</div>
      ) : (
        <TableView
          table={table}
          caption={caption}
          total={serverPaged ? totalCount : undefined}
          serverPaged={serverPaged}
          subRow={subRow}
          rowClassName={rowClassName}
        />
      )}
    </TableFrame>
  );
}
